import { CelebrationSparkles } from "./GameProgressDelta";
import MenuIconTooltip from "./MenuIconTooltip";
import "../components/VocabTooltip.css";
import "./GameProgressDelta.css";
import "./QuestJournalRewards.css";
import {
  buildClaimedRewardParts,
  buildQuestRewardGemImageUrl,
  collectGrantsFromRewards,
  formatQuestRewardClaimedAt,
  formatRelicChanceTooltip,
  getQuestRewardPotion,
} from "../utils/questRewardDisplay";

function getRewardTooltipLabel(reward) {
  if (!reward) return "";
  if (reward.label) return reward.label;
  if (reward.kind === "potion") {
    return getQuestRewardPotion(reward.itemId)?.name || "";
  }
  return "";
}

function RewardMiniature({ reward }) {
  if (!reward) return null;

  const tooltipLabel = getRewardTooltipLabel(reward);
  if (!tooltipLabel) return null;

  if (reward.kind === "gem") {
    return (
      <MenuIconTooltip
        tooltip={tooltipLabel}
        placement="above"
        portal
        className="quest-reward-miniature quest-reward-miniature--gem"
      >
        <img
          src={buildQuestRewardGemImageUrl(reward.itemId)}
          alt=""
          className="quest-reward-miniature__image"
          decoding="sync"
        />
      </MenuIconTooltip>
    );
  }

  if (reward.kind === "potion") {
    const potion = getQuestRewardPotion(reward.itemId);
    return (
      <MenuIconTooltip
        tooltip={tooltipLabel}
        placement="above"
        portal
        className="quest-reward-miniature quest-reward-miniature--potion"
      >
        {potion?.image ? (
          <img
            src={potion.image}
            alt=""
            className="quest-reward-miniature__image"
            decoding="sync"
          />
        ) : (
          <span className="quest-reward-miniature__fallback" aria-hidden="true">
            ⚗
          </span>
        )}
      </MenuIconTooltip>
    );
  }

  return null;
}

function ClaimedRewardPartIcon({ grant }) {
  if (!grant) return null;

  if (grant.kind === "gem") {
    return (
      <img
        src={buildQuestRewardGemImageUrl(grant.itemId)}
        alt=""
        className="quest-journal-rewards-claimed__icon"
        decoding="sync"
      />
    );
  }

  if (grant.kind === "potion") {
    const potion = getQuestRewardPotion(grant.itemId);
    if (potion?.image) {
      return (
        <img
          src={potion.image}
          alt=""
          className="quest-journal-rewards-claimed__icon"
          decoding="sync"
        />
      );
    }
    return (
      <span className="quest-journal-rewards-claimed__icon-fallback" aria-hidden="true">
        ⚗
      </span>
    );
  }

  return null;
}

function ClaimedRewardPart({ part }) {
  return (
    <span className="quest-journal-rewards-claimed__part">
      <span className="quest-journal-rewards-claimed__part-inner">
        <span className="quest-journal-rewards-claimed__qty">{part.quantity}</span>
        <MenuIconTooltip
          tooltip={part.tooltip}
          placement="above"
          portal
          className="quest-journal-rewards-claimed__icon-wrap"
        >
          <ClaimedRewardPartIcon grant={part.grant} />
        </MenuIconTooltip>
      </span>
    </span>
  );
}

function joinFrenchList(nodes) {
  if (nodes.length <= 1) return nodes;
  if (nodes.length === 2) {
    return [
      nodes[0],
      <span key="and" className="quest-journal-rewards-claimed__sep">
        {" "}
        et{" "}
      </span>,
      nodes[1],
    ];
  }

  return nodes.flatMap((node, index) => {
    if (index === 0) return [node];
    if (index === nodes.length - 1) {
      return [
        <span key={`and-${index}`} className="quest-journal-rewards-claimed__sep">
          {" "}
          et{" "}
        </span>,
        node,
      ];
    }
    return [
      <span key={`comma-${index}`} className="quest-journal-rewards-claimed__sep">
        ,{" "}
      </span>,
      node,
    ];
  });
}

function QuestJournalRewardsClaimed({ rewards }) {
  const grants = collectGrantsFromRewards(rewards);
  const parts = buildClaimedRewardParts(grants);
  const claimedAtLabel = formatQuestRewardClaimedAt(rewards.claimedAt);
  const partNodes = parts.map((part, index) => (
    <ClaimedRewardPart
      key={`${part.kind}-${part.grant?.itemId}-${index}`}
      part={part}
    />
  ));

  return (
    <div className="quest-journal-rewards-claimed">
      {parts.length > 0 ? (
        <p className="quest-journal-rewards-claimed__main">
          Vous avez obtenu {joinFrenchList(partNodes)}.
        </p>
      ) : (
        <p className="quest-journal-rewards-claimed__main">
          Vous avez obtenu la récompense de cette quête.
        </p>
      )}

      {rewards.relicWon && rewards.relicLabel ? (
        <p className="quest-journal-rewards-claimed__relic">
          Vous avez découvert la relique{" "}
          <span className="quest-journal-rewards-claimed__relic-name">
            {rewards.relicLabel}
          </span>
          {rewards.collectionTitle ? (
            <>
              {" "}
              de la collection{" "}
              <span className="quest-journal-rewards-claimed__collection">
                {rewards.collectionTitle}
              </span>
            </>
          ) : null}
          .
        </p>
      ) : null}

      {claimedAtLabel ? (
        <p className="quest-journal-rewards-claimed__date">{claimedAtLabel}</p>
      ) : null}
    </div>
  );
}

export default function QuestJournalRewards({
  rewards,
  allCompleted,
  onOpenChest,
  claiming = false,
}) {
  if (!rewards?.primary && rewards?.status !== "claimed") {
    return (
      <p className="quest-journal-rewards-fallback">
        {rewards?.label ||
          "Accomplissez les 4 objectifs pour débloquer le coffre de quête."}
      </p>
    );
  }

  if (rewards.status === "claimed") {
    return <QuestJournalRewardsClaimed rewards={rewards} />;
  }

  const showOpenButton = allCompleted && rewards.status === "openable";

  return (
    <div className="quest-journal-rewards-block">
      <div
        className="quest-journal-rewards-frame game-progress-celebration game-progress-celebration--plain"
        aria-label="Récompenses annoncées"
      >
        {allCompleted ? <CelebrationSparkles /> : null}

        <p className="quest-journal-rewards-relic-line">
          <MenuIconTooltip
            tooltip={formatRelicChanceTooltip(rewards.relicChancePercent, {
              demiLuneActive: Boolean(rewards.demiLuneActive),
            })}
            placement="above"
            portal
            className="quest-journal-rewards-relic-wrap"
          >
            <span className="quest-journal-rewards-relic-text">
              <span className="quest-journal-rewards-relic-count">1</span>{" "}
              <span className="quest-journal-rewards-relic-word">relique</span>
            </span>
          </MenuIconTooltip>
        </p>

        <div className="quest-journal-rewards-lots">
          <span className="quest-journal-rewards-lot">
            <span className="quest-journal-rewards-qty">{rewards.primary.quantity}</span>
            <RewardMiniature reward={rewards.primary} />
          </span>

          {rewards.clover ? (
            <span className="quest-journal-rewards-lot quest-journal-rewards-lot--clover">
              <span className="quest-journal-rewards-qty">{rewards.clover.quantity}</span>
              <RewardMiniature reward={rewards.clover} />
            </span>
          ) : null}
        </div>
      </div>

      {showOpenButton ? (
        <button
          type="button"
          className="quest-journal-rewards-open-btn"
          onClick={onOpenChest}
          disabled={claiming}
        >
          {claiming ? "Ouverture…" : "Ouvrir le coffre"}
        </button>
      ) : null}
    </div>
  );
}
